import { useRef, useState } from 'react'

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { i18n } from '@zet/localization'

import {
  getCrossSelling,
  getLeadDetails,
  getLeadsTransactionHistory,
  submitArn,
} from './functions'

function useLeadDetails(leadId: number) {
  return useQuery(['LeadProgress', leadId], () => getLeadDetails(leadId), {
    enabled: !!leadId,
  })
}

function useLeadsTransactionHistory(
  leadId: number,
  page: number,
  limit: number
) {
  return useInfiniteQuery(
    ['leadsTransaction', leadId],
    ({ pageParam }) => {
      return getLeadsTransactionHistory({
        leadId,
        pageParam: pageParam ?? page,
        limit,
      })
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage?.paginationMeta?.nextPage || undefined
      },
    }
  )
}

function useLeadCrossSelling(leadId: number, page: number, limit: number) {
  const [shouldRefetch, setShouldRefetch] = useState(true)
  const startTime = useRef<number | null>(null)

  const result = useInfiniteQuery(
    ['crossSelling', leadId],
    ({ pageParam }) => {
      if (!startTime.current) {
        startTime.current = Date.now()
      }
      return getCrossSelling({ leadId, pageParam: page ?? pageParam, limit })
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage?.paginationMeta?.nextPage || undefined
      },
      refetchInterval: (data) => {
        if (startTime.current) {
          const currentTime = Date.now()
          const timeDiff = (currentTime - startTime.current) / 1000
          if (timeDiff > 60) {
            setShouldRefetch(false)
            return false
          }
        }
        if (data?.pages?.[0]?.isProcessing) {
          setShouldRefetch(true)
          return 3000
        } else {
          setShouldRefetch(false)
          return false
        }
      },
    }
  )
  return { ...result, shouldRefetch }
}

function useSubmitArn(leadId: string) {
  const queryClient = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: submitArn,
    onSuccess: (data) => {
      // TODO: The reason there are two invalidate queries are because sometimes leadId is string or its a number. This should be fixed
      queryClient.invalidateQueries({ queryKey: ['LeadProgress', leadId] })
      queryClient.invalidateQueries({
        queryKey: ['LeadProgress', String(leadId)],
      })
    },
    onError: (error) => {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description: i18n.t('toastMessages.somethingWentWrong'),
        hideAfter: 3000,
      })
    },
  })
}

export {
  useLeadCrossSelling,
  useLeadDetails,
  useLeadsTransactionHistory,
  useSubmitArn,
}
